import React, { useEffect, useMemo, useState } from "react";
import { api, downloadPdf } from "@/lib/api";
import { toast } from "sonner";
import {
  CalendarClock, Plus, Pencil, Trash2, Download, X, Loader2, Palette,
  Coffee, GripVertical, Droplets, Save, Copy,
} from "lucide-react";

const THEMES = [
  { n: 1, color: "#0b2a5b", name: "Marino" },
  { n: 2, color: "#7c3aed", name: "Violeta" },
  { n: 3, color: "#dc2626", name: "Rojo" },
  { n: 4, color: "#16a34a", name: "Verde" },
  { n: 5, color: "#f97316", name: "Naranja" },
  { n: 6, color: "#0891b2", name: "Cian" },
  { n: 7, color: "#db2777", name: "Rosa" },
  { n: 8, color: "#ca8a04", name: "Ámbar" },
  { n: 9, color: "#4f46e5", name: "Índigo" },
  { n: 10, color: "#0f172a", name: "Grafito" },
];

const DAYS = [
  { key: "lun", label: "Lunes", short: "Lun" },
  { key: "mar", label: "Martes", short: "Mar" },
  { key: "mie", label: "Miércoles", short: "Mié" },
  { key: "jue", label: "Jueves", short: "Jue" },
  { key: "vie", label: "Viernes", short: "Vie" },
  { key: "sab", label: "Sábado", short: "Sáb" },
];

let ridSeq = 0;
const newRid = () => `r${Date.now()}_${ridSeq++}`;

function blankEditor() {
  return {
    id: null,
    title: "",
    level: "",
    group: "",
    days: ["lun", "mar", "mie", "jue", "vie"],
    rows: [
      { _id: newRid(), start: "08:00", end: "08:50", module: "1ª clase", is_break: false },
    ],
    cells: {},
    color: THEMES[0].color,
    formato: 1,
    watermark: true,
  };
}

// Convierte un horario del backend (celdas por índice) al modelo del editor (celdas por _id).
function fromServer(s) {
  const rows = (s.rows || []).map((r) => ({ _id: newRid(), start: r.start || "", end: r.end || "", module: r.module || "", is_break: !!r.is_break }));
  const cells = {};
  (s.rows || []).forEach((r, i) => {
    (s.days || []).forEach((d) => {
      const c = (s.cells || {})[`${i}:${d}`];
      if (c) cells[`${rows[i]._id}:${d}`] = { materia: c.materia || "", docente: c.docente || "", salon: c.salon || "" };
    });
  });
  return {
    id: s.id, title: s.title || "", level: s.level || "", group: s.group || "",
    days: (s.days && s.days.length ? s.days : ["lun", "mar", "mie", "jue", "vie"]),
    rows: rows.length ? rows : blankEditor().rows, cells,
    color: s.color || THEMES[0].color, formato: s.formato || 1, watermark: s.watermark !== false,
  };
}

// Convierte el modelo del editor a payload del backend (celdas por índice).
function toServer(e) {
  const cells = {};
  e.rows.forEach((r, i) => {
    if (r.is_break) return;
    e.days.forEach((d) => {
      const c = e.cells[`${r._id}:${d}`];
      if (c && (c.materia || c.docente || c.salon)) cells[`${i}:${d}`] = { materia: c.materia || "", docente: c.docente || "", salon: c.salon || "" };
    });
  });
  return {
    title: e.title, level: e.level, group: e.group, days: e.days,
    rows: e.rows.map((r) => ({ start: r.start, end: r.end, module: r.module, is_break: r.is_break })),
    cells, color: e.color, formato: e.formato, watermark: e.watermark,
  };
}

export default function Horarios() {
  const [list, setList] = useState(null);
  const [segments, setSegments] = useState({ levels: [], grades: [] });
  const [editor, setEditor] = useState(null);
  const [dup, setDup] = useState(null);

  const load = () => {
    setList(null);
    api.get("/horarios").then((r) => setList(r.data.schedules || [])).catch(() => setList([]));
  };
  useEffect(() => {
    load();
    api.get("/direccion/segments").then((r) => setSegments(r.data || { levels: [], grades: [] })).catch(() => {});
  }, []);

  const openNew = () => setEditor(blankEditor());
  const openEdit = async (id) => {
    try { const { data } = await api.get(`/horarios/${id}`); setEditor(fromServer(data)); }
    catch { toast.error("No se pudo abrir el horario"); }
  };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este horario?")) return;
    try { await api.delete(`/horarios/${id}`); toast.success("Horario eliminado"); load(); }
    catch { toast.error("No se pudo eliminar"); }
  };

  return (
    <div data-testid="horarios-panel">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="font-display font-bold text-lg text-navy flex items-center gap-2"><CalendarClock className="w-5 h-5 text-navy-light" /> Horarios de clases</h2>
          <p className="text-xs text-nardo-500">Arma el horario semanal de cada grupo (Preescolar, Primaria o Secundaria) y expórtalo a PDF.</p>
        </div>
        <button onClick={openNew} data-testid="horario-new" className="inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white px-4 py-2 rounded-lg text-sm font-semibold"><Plus className="w-4 h-4" /> Nuevo horario</button>
      </div>

      {list === null ? (
        <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-navy" /></div>
      ) : list.length === 0 ? (
        <div className="py-16 text-center text-nardo-500 border border-dashed border-nardo-200 rounded-xl" data-testid="horarios-empty">
          Aún no hay horarios. Crea el primero con “Nuevo horario”.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="horarios-grid">
          {list.map((s) => (
            <div key={s.id} className="bg-white border border-nardo-200 rounded-xl p-4" data-testid={`horario-card-${s.id}`}>
              <div className="flex items-start gap-2">
                <span className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-white" style={{ background: s.color || "#0b2a5b" }}><CalendarClock className="w-4 h-4" /></span>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm text-navy truncate">{s.title || "Horario"}</div>
                  <div className="text-[11px] text-nardo-500 truncate">{s.group_label || s.level || "Sin grupo"}</div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 mt-3">
                <button onClick={() => openEdit(s.id)} data-testid={`horario-edit-${s.id}`} className="flex-1 text-[12px] inline-flex items-center justify-center gap-1 border border-navy/30 text-navy hover:bg-navy hover:text-white px-2 py-1.5 rounded-lg font-semibold transition-colors"><Pencil className="w-3.5 h-3.5" /> Editar</button>
                <button onClick={() => setDup(s)} data-testid={`horario-duplicate-${s.id}`} title="Duplicar a otro grupo" className="text-[12px] inline-flex items-center justify-center border border-indigo-300 text-indigo-600 hover:bg-indigo-600 hover:text-white px-2 py-1.5 rounded-lg transition-colors"><Copy className="w-3.5 h-3.5" /></button>
                <button onClick={() => downloadPdf(`/horarios/${s.id}/pdf`, { filename: `horario_${(s.group_label || s.title || "grupo")}.pdf` })} data-testid={`horario-pdf-${s.id}`} title="Descargar PDF" className="text-[12px] inline-flex items-center justify-center gap-1 border border-emerald-500/40 text-emerald-700 hover:bg-emerald-600 hover:text-white px-2 py-1.5 rounded-lg font-semibold transition-colors"><Download className="w-3.5 h-3.5" /></button>
                <button onClick={() => remove(s.id)} data-testid={`horario-delete-${s.id}`} title="Eliminar" className="text-[12px] inline-flex items-center justify-center border border-red-300 text-red-600 hover:bg-red-500 hover:text-white px-2 py-1.5 rounded-lg transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editor && <Editor initial={editor} segments={segments} onClose={() => setEditor(null)} onSaved={() => { setEditor(null); load(); }} />}
      {dup && <DuplicateDialog source={dup} segments={segments} onClose={() => setDup(null)} onDone={() => { setDup(null); load(); }} />}
    </div>
  );
}

function DuplicateDialog({ source, segments, onClose, onDone }) {
  const [level, setLevel] = useState(source.level || "");
  const [group, setGroup] = useState("");
  const [title, setTitle] = useState(`${source.title || "Horario"} (copia)`);
  const [saving, setSaving] = useState(false);

  const grades = useMemo(() => {
    const all = segments.grades || [];
    return level ? all.filter((g) => (g.level || "") === level) : all;
  }, [segments, level]);

  const submit = async () => {
    if (!group) { toast.error("Elige el grupo de destino"); return; }
    setSaving(true);
    try {
      await api.post(`/horarios/${source.id}/duplicate`, { group, level, title });
      toast.success("Horario duplicado");
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo duplicar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[85] bg-black/40 flex items-center justify-center p-4" data-testid="horario-duplicate-dialog">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 text-white" style={{ background: source.color || "#4f46e5" }}>
          <div className="flex items-center gap-2 font-display font-bold"><Copy className="w-5 h-5" /> Duplicar horario</div>
          <button onClick={onClose} data-testid="horario-dup-close" className="p-1.5 rounded-lg hover:bg-white/20"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-nardo-500">Copia <span className="font-semibold text-navy">{source.title || "este horario"}</span> ({source.group_label || "sin grupo"}) a otro grupo. Se copian días, franjas, materias, colores y formato; podrás ajustarlo después.</p>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-nardo-500">Título del nuevo horario</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="horario-dup-title" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy outline-none focus:border-navy" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-nardo-500">Nivel</label>
              <select value={level} onChange={(e) => { setLevel(e.target.value); setGroup(""); }} data-testid="horario-dup-level" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy bg-white outline-none focus:border-navy">
                <option value="">Todos</option>
                {(segments.levels || []).map((l) => <option key={l.value} value={l.value}>{l.value}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-nardo-500">Grupo destino</label>
              <select value={group} onChange={(e) => setGroup(e.target.value)} data-testid="horario-dup-group" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy bg-white outline-none focus:border-navy">
                <option value="">Elegir grupo…</option>
                {grades.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-nardo-200 bg-nardo-50">
          <button onClick={onClose} className="px-4 py-2 text-sm text-nardo-600 hover:text-navy border border-nardo-200 rounded-lg">Cancelar</button>
          <button onClick={submit} disabled={saving} data-testid="horario-dup-submit" className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />} Duplicar
          </button>
        </div>
      </div>
    </div>
  );
}

function Editor({ initial, segments, onClose, onSaved }) {
  const [e, setE] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (patch) => setE((p) => ({ ...p, ...patch }));

  const grades = useMemo(() => {
    const all = segments.grades || [];
    if (!e.level) return all;
    return all.filter((g) => (g.level || "") === e.level);
  }, [segments, e.level]);

  const activeDays = DAYS.filter((d) => e.days.includes(d.key));

  const toggleDay = (key) => set({ days: e.days.includes(key) ? e.days.filter((d) => d !== key) : [...DAYS.filter((d) => d.key === key || e.days.includes(d.key)).map((d) => d.key)] });

  const updateRow = (rid, patch) => set({ rows: e.rows.map((r) => (r._id === rid ? { ...r, ...patch } : r)) });
  const removeRow = (rid) => {
    const cells = { ...e.cells };
    Object.keys(cells).forEach((k) => { if (k.startsWith(`${rid}:`)) delete cells[k]; });
    set({ rows: e.rows.filter((r) => r._id !== rid), cells });
  };
  const addRow = (isBreak) => set({ rows: [...e.rows, { _id: newRid(), start: "", end: "", module: isBreak ? "Receso" : "", is_break: isBreak }] });
  const updateCell = (rid, day, patch) => {
    const key = `${rid}:${day}`;
    set({ cells: { ...e.cells, [key]: { materia: "", docente: "", salon: "", ...(e.cells[key] || {}), ...patch } } });
  };

  const save = async () => {
    if (!e.group) { toast.error("Elige el grupo del horario"); return; }
    setSaving(true);
    try {
      const payload = toServer(e);
      if (e.id) await api.put(`/horarios/${e.id}`, payload);
      else await api.post("/horarios", payload);
      toast.success("Horario guardado");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };

  const cellVal = (rid, day, field) => (e.cells[`${rid}:${day}`] || {})[field] || "";
  const inputCls = "w-full border border-nardo-200 rounded px-1.5 py-1 text-[11px] outline-none focus:border-navy";

  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-start justify-center overflow-auto p-2 sm:p-4" data-testid="horario-editor">
      <div className="bg-nardo-50 rounded-2xl w-full max-w-6xl my-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 rounded-t-2xl text-white sticky top-0 z-10" style={{ background: e.color }}>
          <div className="flex items-center gap-2 font-display font-bold"><CalendarClock className="w-5 h-5" /> {e.id ? "Editar horario" : "Nuevo horario"}</div>
          <button onClick={onClose} data-testid="horario-close" className="p-1.5 rounded-lg hover:bg-white/20"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 sm:p-5 space-y-4">
          {/* Datos generales */}
          <div className="bg-white border border-nardo-200 rounded-xl p-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="lg:col-span-2">
              <label className="text-[10px] uppercase tracking-wider text-nardo-500">Título del horario</label>
              <input value={e.title} onChange={(ev) => set({ title: ev.target.value })} data-testid="horario-title" placeholder="Ej. Horario 1° A Primaria" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy outline-none focus:border-navy" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-nardo-500">Nivel</label>
              <select value={e.level} onChange={(ev) => set({ level: ev.target.value, group: "" })} data-testid="horario-level" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy bg-white outline-none focus:border-navy">
                <option value="">Todos</option>
                {(segments.levels || []).map((l) => <option key={l.value} value={l.value}>{l.value}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-nardo-500">Grupo</label>
              <select value={e.group} onChange={(ev) => set({ group: ev.target.value })} data-testid="horario-group" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy bg-white outline-none focus:border-navy">
                <option value="">Elegir grupo…</option>
                {grades.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
              </select>
            </div>
          </div>

          {/* Formato: días, color, marca de agua */}
          <div className="bg-white border border-nardo-200 rounded-xl p-4 space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-nardo-500 mb-1.5">Días a incluir</div>
              <div className="flex flex-wrap gap-1.5">
                {DAYS.map((d) => {
                  const on = e.days.includes(d.key);
                  return (
                    <button key={d.key} onClick={() => toggleDay(d.key)} data-testid={`horario-day-${d.key}`}
                      className="text-[12px] px-3 py-1.5 rounded-full border font-semibold transition-colors"
                      style={on ? { background: e.color, borderColor: e.color, color: "#fff" } : { background: "#fff", borderColor: "#e2e8f0", color: "#475569" }}>{d.label}</button>
                  );
                })}
              </div>
            </div>
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-nardo-500 mb-1.5 flex items-center gap-1"><Palette className="w-3 h-3" /> Color del formato</div>
                <div className="flex flex-wrap gap-1.5">
                  {THEMES.map((t) => (
                    <button key={t.n} onClick={() => set({ color: t.color, formato: t.n })} title={t.name} data-testid={`horario-color-${t.n}`}
                      className={`w-7 h-7 rounded-full border-2 transition-transform ${e.color === t.color ? "scale-110 ring-2 ring-offset-1 ring-navy" : "border-white"}`} style={{ background: t.color }} />
                  ))}
                </div>
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-navy cursor-pointer" data-testid="horario-watermark-label">
                <input type="checkbox" checked={e.watermark} onChange={(ev) => set({ watermark: ev.target.checked })} data-testid="horario-watermark" className="accent-navy w-4 h-4" />
                <Droplets className="w-4 h-4 text-navy-light" /> Marca de agua con el logo del colegio
              </label>
            </div>
            <p className="text-[11px] text-nardo-400">El PDF llevará el logo del colegio arriba (como en las planeaciones) y los encabezados en el color elegido.</p>
          </div>

          {/* Rejilla del horario */}
          <div className="bg-white border border-nardo-200 rounded-xl p-3 overflow-x-auto">
            <table className="w-full border-collapse" data-testid="horario-grid">
              <thead>
                <tr>
                  <th className="text-[11px] uppercase tracking-wider text-white px-2 py-2 rounded-tl-lg text-left min-w-[130px]" style={{ background: e.color }}>Franja / Hora</th>
                  {activeDays.map((d) => (
                    <th key={d.key} className="text-[11px] uppercase tracking-wider text-white px-2 py-2 min-w-[150px]" style={{ background: e.color }}>{d.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {e.rows.map((r) => (
                  <tr key={r._id} className="align-top border-b border-nardo-100">
                    <td className="px-1.5 py-1.5 border-r border-nardo-100 bg-nardo-50/50">
                      <div className="flex items-center gap-1 mb-1">
                        <GripVertical className="w-3.5 h-3.5 text-nardo-300 shrink-0" />
                        <input value={r.module} onChange={(ev) => updateRow(r._id, { module: ev.target.value })} data-testid={`horario-row-module-${r._id}`} placeholder={r.is_break ? "Receso" : "Módulo"} className={inputCls} />
                      </div>
                      <div className="flex items-center gap-1">
                        <input value={r.start} onChange={(ev) => updateRow(r._id, { start: ev.target.value })} data-testid={`horario-row-start-${r._id}`} type="time" className={`${inputCls} w-[70px]`} />
                        <span className="text-nardo-400 text-[10px]">–</span>
                        <input value={r.end} onChange={(ev) => updateRow(r._id, { end: ev.target.value })} data-testid={`horario-row-end-${r._id}`} type="time" className={`${inputCls} w-[70px]`} />
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <button onClick={() => updateRow(r._id, { is_break: !r.is_break })} data-testid={`horario-row-break-${r._id}`} className={`text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${r.is_break ? "bg-amber-100 text-amber-700" : "text-nardo-400 hover:text-amber-700"}`}><Coffee className="w-3 h-3" /> Receso</button>
                        <button onClick={() => removeRow(r._id)} data-testid={`horario-row-del-${r._id}`} className="text-[10px] text-red-500 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                    {r.is_break ? (
                      <td colSpan={activeDays.length} className="px-2 py-2 text-center text-[12px] font-semibold text-amber-700 bg-amber-50">{r.module || "Receso"}</td>
                    ) : (
                      activeDays.map((d) => (
                        <td key={d.key} className="px-1.5 py-1.5 border-l border-nardo-100">
                          <input value={cellVal(r._id, d.key, "materia")} onChange={(ev) => updateCell(r._id, d.key, { materia: ev.target.value })} data-testid={`horario-cell-materia-${r._id}-${d.key}`} placeholder="Materia" className={`${inputCls} font-semibold mb-1`} style={{ color: e.color }} />
                          <input value={cellVal(r._id, d.key, "docente")} onChange={(ev) => updateCell(r._id, d.key, { docente: ev.target.value })} data-testid={`horario-cell-docente-${r._id}-${d.key}`} placeholder="Docente" className={`${inputCls} mb-1`} />
                          <input value={cellVal(r._id, d.key, "salon")} onChange={(ev) => updateCell(r._id, d.key, { salon: ev.target.value })} data-testid={`horario-cell-salon-${r._id}-${d.key}`} placeholder="Salón / Aula" className={inputCls} />
                        </td>
                      ))
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex gap-2 mt-3">
              <button onClick={() => addRow(false)} data-testid="horario-add-row" className="text-[12px] inline-flex items-center gap-1 border border-navy/30 text-navy hover:bg-navy hover:text-white px-3 py-1.5 rounded-lg font-semibold transition-colors"><Plus className="w-3.5 h-3.5" /> Agregar franja</button>
              <button onClick={() => addRow(true)} data-testid="horario-add-break" className="text-[12px] inline-flex items-center gap-1 border border-amber-400 text-amber-700 hover:bg-amber-500 hover:text-white px-3 py-1.5 rounded-lg font-semibold transition-colors"><Coffee className="w-3.5 h-3.5" /> Agregar receso</button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-nardo-200 bg-white rounded-b-2xl sticky bottom-0">
          <button onClick={onClose} className="px-4 py-2 text-sm text-nardo-600 hover:text-navy border border-nardo-200 rounded-lg">Cancelar</button>
          <button onClick={save} disabled={saving} data-testid="horario-save" className="inline-flex items-center gap-2 text-white px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-50" style={{ background: e.color }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar horario
          </button>
        </div>
      </div>
    </div>
  );
}
